import { without } from 'lodash';
import { SyntheticEvent, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { v4 as uuidV4 } from 'uuid';

import { useLoggedInUserRoles } from '~/components/Auth';
import { useConfirmDialog } from '~/components/DialogsProvider';
import { useMenu } from '~/hooks/useMenu';
import { useVisible } from '~/hooks/useVisible';
import {
  WorkspaceSetupFormSettings,
  WorkspaceSetupMode,
} from '~/scenes/MyWorkspace/components/WorkspaceToolbar/components/WorkspaceToolbarReadMode/components/WorkspaceSetupDialog';
import { useWorkspaceInitializationContext } from '~/scenes/MyWorkspace/context';
import { WorkspaceType } from '~/scenes/MyWorkspace/data';
import {
  useUpdateWorkspaceSettings,
  useWorkspaceSettings,
} from '~/scenes/MyWorkspace/hooks';
import { isRoleHRAdminFull } from '~/services/Permissions';

import { enterManageWorkspace } from '../../../../layoutSlice';
import {
  WorkspaceSettings,
  defaultUserSettings,
} from '../../../../userSettings';

export interface UseManageWorkspacesOutput {
  menuAnchorEl: Element | null;
  handleMenuOpen: (event: SyntheticEvent) => void;
  handleMenuClose: () => void;
  workspaces: WorkspaceSettings[];
  activeWorkspace: WorkspaceSettings;
  handleCreateWorkspace: () => void;
  handleManageWorkspace: () => void;
  handleDuplicateWorkspace: () => void;
  handleActiveWorkspaceChange: (value: string) => void;
  handleActiveWorkspaceDelete: () => void;
  isCurrentActiveWidgetADefault: boolean;
  canEditWorkspace: (type?: string) => boolean;
  adminMode: {
    visible: boolean;
    enabled: boolean;
    selected: boolean;
    toggle: () => void;
  };
  tabsWorkspaces: WorkspaceSettings[];
}

type PropsType = {
  handleSetupDialogOpen: (preset: WorkspaceSetupFormSettings) => void;
};

export const useWorkspaceToolbarReadMode = (
  props: PropsType,
): UseManageWorkspacesOutput => {
  const { handleSetupDialogOpen } = props;

  const {
    anchorEl: menuAnchorEl,
    open: handleMenuOpen,
    close: handleMenuClose,
  } = useMenu();

  const { workspaces, activeWorkspace, adminOnlyWorkspaces, hasData } =
    useWorkspaceSettings();

  // Handle Admin Only Workspaces
  const hasAdminOnlyWorkspaces = useMemo(
    () => adminOnlyWorkspaces.length > 0,
    [adminOnlyWorkspaces],
  );
  const adminOnlyIds = useMemo(
    () => adminOnlyWorkspaces.map((workspace) => workspace.id),
    [adminOnlyWorkspaces],
  );
  const activeWorkspaceIsAdminOnly = useMemo(
    () => adminOnlyIds.includes(activeWorkspace.id),
    [adminOnlyIds, activeWorkspace],
  );

  // Handle AdminMode
  const {
    isVisible: isAdminMode,
    toggle: toggleAdminMode,
    show: enableAdminMode,
  } = useVisible(activeWorkspaceIsAdminOnly);

  useEffect(() => {
    if (activeWorkspaceIsAdminOnly && !isAdminMode) {
      enableAdminMode();
    }
  }, [activeWorkspaceIsAdminOnly, isAdminMode, enableAdminMode]);

  // Handle Tabs in Regular & AdminMode
  const tabsWorkspaces = useMemo((): WorkspaceSettings[] => {
    if (!isAdminMode && hasAdminOnlyWorkspaces) {
      return workspaces.filter(
        (workspace) => !adminOnlyIds.includes(workspace.id),
      );
    }
    return workspaces;
  }, [isAdminMode, workspaces, adminOnlyIds, hasAdminOnlyWorkspaces]);

  // Handle workspace changes
  const dispatch = useDispatch();
  const roles = useLoggedInUserRoles();
  const isHRAdmin = isRoleHRAdminFull(roles);
  const canEditWorkspace = (type?: string) => {
    const wsType = type || activeWorkspace.type;

    return (
      (wsType === WorkspaceType.Company && isHRAdmin) ||
      wsType !== WorkspaceType.Company
    );
  };

  const handleCreateWorkspace = useCallback(() => {
    handleSetupDialogOpen({
      mode: WorkspaceSetupMode.Create,
      payload: {
        id: uuidV4(),
        name: '',
        widgets: [],
        type: WorkspaceType.Default,
        restrictToRoles: [],
      },
    });
  }, [handleSetupDialogOpen]);

  const handleManageWorkspace = useCallback(() => {
    dispatch(enterManageWorkspace(activeWorkspace));
    handleMenuClose();
  }, [dispatch, activeWorkspace, handleMenuClose]);

  const handleDuplicateWorkspace = useCallback(() => {
    handleSetupDialogOpen({
      mode: WorkspaceSetupMode.Duplicate,
      payload: {
        ...activeWorkspace,
        id: uuidV4(),
      },
    });

    handleMenuClose();
  }, [handleSetupDialogOpen, activeWorkspace, handleMenuClose]);

  const { updateWorkspacesSettings } = useUpdateWorkspaceSettings();

  const handleActiveWorkspaceChange = useCallback(
    (value: string) => {
      updateWorkspacesSettings({
        workspaces,
        activeWorkspaceId: value,
      });
    },
    [updateWorkspacesSettings, workspaces],
  );

  const { isInitialized, setIsInitialized } =
    useWorkspaceInitializationContext();

  useEffect(() => {
    if (!isInitialized && !hasData) {
      updateWorkspacesSettings({
        workspaces,
        activeWorkspaceId: workspaces[0] ? workspaces[0].id : undefined,
      });
      setIsInitialized(true);
    }
  }, [
    isInitialized,
    setIsInitialized,
    workspaces,
    updateWorkspacesSettings,
    hasData,
  ]);

  const { t } = useTranslation();
  const confirm = useConfirmDialog();

  const handleActiveWorkspaceDelete = useCallback(() => {
    confirm(t('activeWorkspaceDeleteConfirmationMessage'), (confirmed) => {
      if (confirmed) {
        updateWorkspacesSettings({
          workspaces: without(workspaces, activeWorkspace),
          activeWorkspaceId: undefined,
        });
      }
    });

    handleMenuClose();
  }, [
    t,
    confirm,
    updateWorkspacesSettings,
    workspaces,
    activeWorkspace,
    handleMenuClose,
  ]);

  const isCurrentActiveWidgetADefault = !!defaultUserSettings(
    roles,
  ).workspaces.find((w) => w.id === activeWorkspace.id);

  return {
    menuAnchorEl,
    handleMenuOpen,
    handleMenuClose,
    workspaces,
    activeWorkspace,
    handleCreateWorkspace,
    handleManageWorkspace,
    handleDuplicateWorkspace,
    handleActiveWorkspaceChange,
    handleActiveWorkspaceDelete,
    isCurrentActiveWidgetADefault,
    canEditWorkspace,
    tabsWorkspaces,
    adminMode: {
      visible: hasAdminOnlyWorkspaces && isHRAdmin,
      enabled: !activeWorkspaceIsAdminOnly,
      selected: isAdminMode,
      toggle: toggleAdminMode,
    },
  };
};
